<template>
    <div class="input-block">
        <label class="label">{{ labelName }}</label>
        <vue-tel-input
            :value="telValue"
            :placeholder="customPlaceholder"
            :inputId="forId"
            :mode="inputMode"
            :preferred-countries="['us', 'gb']"
            :class="{ invalid: phone_valid.valid === false && phone_valid.touched }"
            ref="input"
            class="input-phone"
            required
            @input="valueChanged"
            @country-changed="onCountryChanged"
            @validate="phone_valid.valid = $event.valid"
        />
        <div class="error-message" v-if="error">{{ error }}</div>
    </div>
</template>

<script>
import { VueTelInput } from 'vue-tel-input'
import PhoneNumber from 'awesome-phonenumber'
import IMask from 'imask'

export default {
    name: 'InputTelField',
    components: {
        VueTelInput,
    },
    props: ['labelName', 'forId', 'initialValue', 'placeholder', 'error'],
    data() {
        return {
            telValue: null,
            inputMode: 'international',
            customPlaceholder: this.placeholder,
            mask: null,
            phone_valid: {
                valid: false,
                touched: false,
            },
        }
    },
    watch: {
        $props: {
            handler() {
                this.checkProps()
            },
            deep: true,
            immediate: true,
        },
        phone_valid: {
            deep: true,
            immediate: true,
            handler(n) {
                this.$emit('valid', !(n.valid === false && n.touched))
            },
        },
    },
    mounted() {
        this.checkProps()

        this.mask = IMask(this.$refs.input.$el.querySelector('.vti__input'), {
            mask: '+1 000 000-0000',
        })
    },
    methods: {
        checkProps() {
            if (this.initialValue) {
                this.telValue = this.initialValue
            }
        },
        valueChanged(val) {
            this.telValue = val
            this.$emit('input', val)

            this.phone_valid.touched = true
        },
        onCountryChanged(payload) {
            const phoneExample = PhoneNumber.getExample(payload.iso2, 'mobile').getNumber(this.inputMode)
            const d = payload.dialCode
            const code = '+' + (d.length === 4 ? d.substr(0, 1) + ' ' + d.substr(1) : d)
            this.customPlaceholder = code + phoneExample.split(code)[1].replace(/[0-9]/g, '0')

            this.mask.updateOptions({
                mask: this.customPlaceholder,
            })

            if (this.telValue) {
                this.valueChanged(code)
                this.mask.value = code
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import '../../../../../../assets/scss/global-styles';

.input-block {
    padding: 10px 20px;

    position: relative;

    &:hover,
    &:active {
        background: rgba(192, 192, 201, 0.12) 0% 0% no-repeat padding-box;
        border-radius: 6px;
    }

    .label {
        font-family: 'Gilroy-Regular', sans-serif;
        font-size: 12px;
        line-height: 14px;
        text-align: left;
        color: $mainTextBlueColor;
    }

    >>> .input-phone.vue-tel-input {
        border: none;
        outline: none;
        box-shadow: none;

        &:active {
            box-shadow: none;
        }

        .vti__dropdown {
            padding-left: 0;
            padding-right: 15px;
            outline: none;
            position: relative;

            &:hover,
            &.open {
                background: transparent;
            }

            &.open {
                .vti__dropdown-list {
                    z-index: 500;
                    background: #f5f5f6;
                    border-radius: 7px;
                    color: $mainTextBlueColor;
                    border: 0.5px solid #ffffff27;
                    font-family: 'Gilroy-Regular', sans-serif;

                    &::-webkit-scrollbar {
                        width: 5px;
                    }

                    &::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.1);
                        border-radius: 3px;
                    }

                    &::-webkit-scrollbar-thumb:hover {
                        background: rgba(255, 255, 255, 0.2);
                    }

                    .vti__dropdown-item {
                        &:hover {
                            background-color: rgba(255, 255, 255, 0.2);
                        }

                        &.highlighted {
                            background-color: rgba(255, 255, 255, 0.2);
                        }
                    }
                }

                .vti__dropdown-arrow {
                    background-image: url('~@/assets/main_images/inverse-top-bottom-icon.png');
                }
            }

            .vti__flag {
                /*padding-left: 0;*/
            }

            .vti__dropdown-arrow {
                content: '';
                color: transparent;
                position: absolute;
                top: 50%;
                right: 0;
                transform: translateY(-50%);
                line-height: 10px;
                font-size: 6.5px;
                width: 11px;
                height: 6.5px;
                background-image: url('~@/assets/main_images/top-bottom-icon.png');
                background-size: contain;
                background-position: center center;
                background-repeat: no-repeat;

                &:before,
                &:after {
                    content: '';
                }
            }
        }

        input.vti__input {
            background: transparent;
            border: none;
            color: $mainTextBlueColor;
            padding: 10px 10px 10px 0;
            margin-left: 10px;
            font-family: 'Gilroy-SemiBold', sans-serif;
            font-size: 18px;
            line-height: 22px;
            text-align: left;
            outline: none;
            width: 100%;

            &:hover {
                border-radius: 6px;
            }

            &::placeholder {
                font-family: 'Gilroy-SemiBold', sans-serif;
                font-size: 16px;
                line-height: 19px;
                text-align: left;
                color: $mainTextBlueColor;
            }
        }

        &.invalid {
            input.vti__input {
                color: $mainErrorColor;
            }
        }
    }

    .error-message {
        font-family: 'Gilroy-Regular', sans-serif;
        font-size: 12px;
        line-height: 12px;
        letter-spacing: 0.4px;
        color: $mainErrorColor;
        position: absolute;
        bottom: 0;
        left: 20px;
        animation-name: errorHandle;
        animation-duration: 0.5s;
    }

    @keyframes errorHandle {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
}

@media (max-width: 768px) {
    .input-block {
        max-width: unset;
        padding: 10px 20px;
        flex-basis: unset;
        width: 100%;

        .label {
            font-size: 14px;
            line-height: 17px;
        }

        >>> .input-phone.vue-tel-input {
            input.vti__input {
                max-width: 100%;
                font-family: 'Gilroy-SemiBold', sans-serif;
                font-size: 16px;
                line-height: 20px;

                &::placeholder {
                    font-size: 16px;
                    line-height: 20px;
                }
            }
        }

        .error-message {
            left: 10px;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/pages/index/components/shared/forms_entities/form-fields/InputTelField.vue